// apps/spa/src/components/commands/CommandRowActions.tsx
// DropdownMenu for each row in CommandTable.
//
// Items:
//   * Edit               — navigate to /commands/:id/edit
//   * Retry registration — only shown when lastRegisterError !== null (Pending Discord state)
//   * Delete             — raises into parent CommandsList to show DeleteCommandDialog (red)

import { useNavigate } from 'react-router';
import { MoreVertical, Pencil, Trash2, RefreshCw } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useRetryRegistration } from '@/hooks/useCommands';
import type { SlashCommand } from '@guardian/shared';

export interface CommandRowActionsProps {
  command: SlashCommand;
  onDelete: (id: string) => void;
}

export function CommandRowActions({ command, onDelete }: CommandRowActionsProps) {
  const navigate = useNavigate();
  const retry = useRetryRegistration();

  const isPendingDiscord = !!command.lastRegisterError;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Actions for /${command.name}`}
        className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MoreVertical className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate(`/commands/${command.id}/edit`)}>
          <Pencil className="size-4" /> Edit
        </DropdownMenuItem>
        {isPendingDiscord && (
          <DropdownMenuItem
            onClick={() => retry.mutate(command.id)}
            disabled={retry.isPending}
          >
            <RefreshCw className="size-4" /> Retry registration
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => onDelete(command.id)}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="size-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
